import React from "react";
import { Card, CardContent } from "../ui/card";
import {
  MousePointerClick,
  ArrowRightToLine,
  LifeBuoy,
  Headset,
  PhoneCall,
  CalendarClock,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import BehavioralActionCard from "./BehavioralActionCard";
import { SectionHeader } from "./HelperText";
import { useEmailBehavioralActionsData } from "../../hooks/useEmailBehavioralActionsData";

/**
 * EmailBehavioralActionsView — the Email tab's "Actions" view (P1 SLICE 3).
 *
 * Surfaces the eight frozen Behavioral LPE action metrics from the real
 * `GET /email-delivery/dashboard/behavioral-actions` contract (P1
 * IMPLEMENTATION SLICE 2, PR #358) — a sibling of Overview/Performance,
 * never folded into either.
 *
 * Deliberately does NOT reuse EmailFunnelCard: these eight metrics are
 * independent action counts, not a sequential funnel. No "% of previous"
 * conversion badge, no proportional bar, no arrow/connector, no stage
 * numbering appears anywhere on this view. Three section headings group
 * related metrics for scanability only — a heading communicates
 * relatedness, never an additive or sequential relationship. In
 * particular: Online Enrollment Actions and Continue Online Handoff
 * Arrivals are NOT additive/equivalent; Agent Assistance Referrals does
 * NOT "convert into" Agent Contact Actions; Call/Schedule Agent Actions
 * are described only as a breakdown of Agent Contact Actions (their sum
 * equals it, per frozen backend membership), never as funnel stages
 * beneath it.
 *
 * Each metric's PRIMARY number is `value` (raw canonical EVENT count,
 * frozen `grain: "event_count"`) -- see BehavioralActionCard.jsx for how
 * `attempts_with_action`/`journeys_with_action` are rendered as
 * secondary, lower-weight detail. A successful all-zero response is valid
 * data, rendered exactly like any other real value -- never treated as
 * the empty state below (which is reserved for `metrics === null` with
 * no error, i.e. before the first real response has ever arrived under
 * an `enabled` fetch).
 *
 * These granular counts can legitimately exceed the existing frozen High
 * Intent KPI (Overview's own `distinct quote_attempt_id_claim` over its
 * own six-member event set) -- this view never presents any number here
 * as a component that sums to, replaces, or reconciles against High
 * Intent.
 */

const METRIC_SECTIONS = [
  {
    title: "Online",
    metrics: [
      { key: "online_enrollment_actions", title: "Online Enrollment Actions", icon: MousePointerClick },
      { key: "continue_online_handoff_arrivals", title: "Continue Online Handoff Arrivals", icon: ArrowRightToLine },
    ],
  },
  {
    title: "Agent Assistance",
    helper: "Call Agent Actions and Schedule Agent Actions are a breakdown of Agent Contact Actions -- their values sum to it. None of the four metrics below implies a prior one converted into the next.",
    metrics: [
      { key: "agent_assistance_referrals", title: "Agent Assistance Referrals", icon: LifeBuoy },
      { key: "agent_contact_actions", title: "Agent Contact Actions", icon: Headset },
      { key: "call_agent_actions", title: "Call Agent Actions", icon: PhoneCall },
      { key: "schedule_agent_actions", title: "Schedule Agent Actions", icon: CalendarClock },
    ],
  },
  {
    title: "Save Quote",
    metrics: [
      { key: "save_quote_opens", title: "Save Quote Opens", icon: Bookmark },
      { key: "save_quote_submissions", title: "Save Quote Submissions", icon: BookmarkCheck },
    ],
  },
];

function BehavioralActionsGridSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="behavioral-actions-skeleton">
      {Array.from({ length: 8 }).map((_, i) => (
        <Card key={i} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
            <div className="h-3 w-24 rounded bg-white/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function EmailBehavioralActionsView({
  period,
  customRange,
  environment,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
}) {
  const { loading, error, metrics } = useEmailBehavioralActionsData({
    period,
    customRange,
    environment,
    governedBroadcastId: governedBroadcastId === "all" ? null : governedBroadcastId,
    governedAudienceId: governedAudienceId === "all" ? null : governedAudienceId,
    governedProgramId: governedProgramId === "all" ? null : governedProgramId,
    governedCampaignId: governedCampaignId === "all" ? null : governedCampaignId,
    governedState: governedState === "all" ? null : governedState,
  });

  const hasMetrics = metrics !== null;

  return (
    <div className="space-y-6" data-testid="email-behavioral-actions-view">
      {error && (
        <Card className="glass border-red-500/20" data-testid="email-behavioral-actions-error">
          <CardContent className="p-4 text-sm text-red-300">
            Failed to load Email Behavioral Actions data: {error}
          </CardContent>
        </Card>
      )}

      {!error && loading && <BehavioralActionsGridSkeleton />}

      {!error && !loading && hasMetrics && (
        <>
          <SectionHeader
            title="Actions"
            helper="Independent behavioral action counts for the selected Period, Environment, Broadcast, Audience, Program, Campaign, and State selection -- not a sequential funnel. Each count can exceed the Overview funnel's High Intent total; these numbers are never components that sum to it."
          />
          {METRIC_SECTIONS.map((section) => (
            <div key={section.title}>
              <p className="text-sm font-semibold text-slate-200 mb-1">{section.title}</p>
              {section.helper && <p className="text-xs text-slate-400 mb-3">{section.helper}</p>}
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
                data-testid={`behavioral-actions-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {section.metrics.map((def) => {
                  const metric = metrics[def.key];
                  return (
                    <BehavioralActionCard
                      key={def.key}
                      title={def.title}
                      value={metric?.value}
                      attemptsWithAction={metric?.attempts_with_action}
                      journeysWithAction={metric?.journeys_with_action}
                      icon={def.icon}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {!error && !loading && !hasMetrics && (
        <Card className="glass border-white/10" data-testid="email-behavioral-actions-empty">
          <CardContent className="p-8 text-center text-slate-400">
            <p className="text-lg">No Actions data yet</p>
            <p className="text-sm mt-2">
              Metrics will appear here once Email data is available for this selection.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
